import { themeColors } from "../../../themeColors";
/**
 * @file   src\components\AgentDashboard\InventoryListing.js
 * @brief  This file is responsible for Inventory listing in agent dashboard section
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState } from "react";
import useIsMobile from "../../../hooks/useIsMobile";
import MobileActionModal from "./MobileActionModal";
import {
  formateDate,
  loadPropertTypeImage,
  thousandSeperatedValue,
} from "../../../helpers/common";
import ActionDropdown from "../ActionDropdown";
import TableMoreIconVertical from "../../../images/table-more-vertical-icon.svg";
import EditIcon from "../../../images/dpdown-edit-client-icon.svg";
import TrashIcon from "../../../images/dpdown-trash-icon.svg";
import { GenericTable, Paginator } from "@/components";
import TableHeader from "../TableHeader";
import { pageURLs } from "../../../constants/pageURLs";
import { useNavigate } from "react-router-dom";
import { formatInventoryAddress } from "@/helpers";
import { emptyPropertyPlaceholderImages } from "@/components/Onboarding/Dashboard/v2/PropertyInventoryListing";

const InventoryListing = (props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [actionModal, setActionModal] = useState({ show: false, itemId: null });
  const {
    showNoResult,
    handleSort,
    itemList,
    onItemEdit,
    onItemDelete,
    sort,
    fromInvestorTab,
    loading,
    currentPage,
    hasNextPage,
    onNextPage,
    onPrevPage,
    pageSize,
    onPageSizeChange,
  } = props;

  const columns = [
    {
      label: "Date",
      sortKey: "date",
      sorting: "Yes",
      cell: (inventry) => {
        const createdOn = _.get(inventry, "CreatedOn", "");
        return formateDate(createdOn, "shortMonth");
      },
    },
    {
      label: "Address",
      sortKey: "address",
      sorting: "Yes",
      cell: (_inventory) => {
        const photo = _.get(_inventory, "photo", null);

        const emptyPlaceholderImage = emptyPropertyPlaceholderImages[Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)];
        const emptyPlaceholderImageUrl = require(`@/images/${emptyPlaceholderImage}`);

        const invPhoto = photo || emptyPlaceholderImageUrl;

        const location = _.get(_inventory, "Location", "");

        return location ? (
          <a
            href={void 0}
            role="button"
            className="txt-no-underline client-name-style"
          >
            <div className="d-flex align-items-center justify-content-start">
              <div className="table-address-img">
                <img src={invPhoto} alt="inventoryImage" />
              </div>
              <span className="d-inline-block client-name-style txt-overflow-style">
                {formatInventoryAddress(location)}
              </span>
            </div>
          </a>
        ) : null;
      },
    },
    {
      label: "Potential Buyers",
      sortKey: "potentialbuyers",
      sorting: "Yes",
      cell: (inventory) => {
        const potentialBuyers = _.get(inventory, "potentialBuyers", 0);
        const invId = _.get(inventory, "Inv_Id", "");
        const hasPotentialBuyers = potentialBuyers > 0;
        const representingAsSeller = _.get(
          inventory,
          "Represent_as_Seller",
          "",
        );

        const color = representingAsSeller ? "green" : "blue";

        const handleNavigateToMatches = (e) => {
          e.stopPropagation();
          if (!hasPotentialBuyers) return;
          const isPublished = _.get(inventory, "IsPublished", false);
          handleInventoryItemClick(invId, "Matches", isPublished);
        };

        return (
          <div
            onClick={handleNavigateToMatches}
            className={`want-ad-tag medium-${color}-tag-bg-border${hasPotentialBuyers ? ` medium-${color}-tag-bg-border-clickable` : ""} mb-0`}
          >
            {thousandSeperatedValue(potentialBuyers) || 0} Matches
          </div>
        );
      },
    },
    {
      label: "Price",
      sortKey: "price",
      sorting: "Yes",
      cell: (inventry) => {
        const price = _.get(inventry, "AskingPrice_Sell", 0);

        return (
          <p className="mb-0">
            {Number(price).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        );
      },
    },
    {
      label: "Property Status",
      sortKey: "property_status",
      sorting: "Yes",
      cell: (inventry) => {
        const isDraft = _.get(inventry, "IsSavedDraft", "") === 1;
        const isSold = _.get(inventry, "IsSold", "") === 1;

        let status = "Active";
        if (isDraft) status = "Draft";
        else if (isSold) status = "Sold";

        return <p style={{ marginBottom: 0 }}>{status}</p>;
      },
    },
    {
      label: "Type",
      sortKey: "type",
      sorting: "Yes",
      cell: (inventry) => {
        const representingAsSeller = _.get(inventry, "Represent_as_Seller", "");
        return (
          <div
            className="d-flex align-items-center justify-content-start"
            style={{
              backgroundColor: `${representingAsSeller ? themeColors.accentf100 : themeColors.secondary120}`,
              width: "fit-content",
              paddingRight: "5px",
              paddingLeft: "5px",
            }}
          >
            <div className="property-location-icon">
              {loadPropertTypeImage(representingAsSeller)}
            </div>
            <span
              style={{
                color: `${representingAsSeller ? themeColors.accentf600 : themeColors.secondary600}`,
                fontWeight: 500,
              }}
            >
              {representingAsSeller ? "POTENTIAL SELLER" : "SELLER"}
            </span>
          </div>
        );
      },
    },
    {
      label: "Client",
      sorting: "Yes",
      sortKey: "client",
      cell: (inventry) => {
        if (fromInvestorTab) return null;
        const firstName = _.get(inventry, "FirstName", null);
        const lastName = _.get(inventry, "LastName", null);
        const isAgent = _.get(inventry, "Represent_as_Seller", "") == 1;
        const invId = _.get(inventry, "Inv_Id", "");

        const clientName =
          firstName || lastName
            ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
            : "";

        const client = isAgent ? "Agent" : clientName;

        return (
          <div className="agent-want-listing-dropdown" onClick={(e) => e.stopPropagation()}>
            <p className="mb-0">{client}</p>
            <div className="social-btns-wrap no-bg-toggle ms-1">
              {isMobile ? (
                <img
                  src={TableMoreIconVertical}
                  alt="actions"
                  role="button"
                  onClick={() => setActionModal({ show: true, itemId: invId })}
                />
              ) : (
                <ActionDropdown
                  onItemEdit={onItemEdit}
                  onItemDelete={onItemDelete}
                  editText="Edit"
                  deleteText="Delete"
                  itemId={invId}
                  buyerListLinkText="BuyerListing Form"
                />
              )}
            </div>
          </div>
        );
      },
    },
  ];

  /**
   * To handle inventory item click
   * @param {*} inventoryId
   * @param {string} tabKey - Optional tab key to navigate to specific tab
   */
  const handleInventoryItemClick = (inventoryId, tabKey = null, isPublished) => {

    if (!isPublished) {
      return onItemEdit(inventoryId);
    }

    const url = tabKey
      ? `${pageURLs.inventoryDetails}/${inventoryId}?tabKey=${tabKey}`
      : `${pageURLs.inventoryDetails}/${inventoryId}`;
    navigate(url, { state: { isFromAgentDashboard: true } });
  };

  return (
    <div className="dashboard-table-wrap">
      <div className="table-responsive wantad-table-responsive">
        {showNoResult && itemList !== null && itemList?.length > 0 ? (
          <GenericTable
            data={itemList}
            columns={columns}
            className="table"
            renderHeader={() => (
              <TableHeader
                columns={columns}
                handleSort={handleSort}
                sort={sort}
              />
            )}
            renderRow={(item) =>
              columns.map((col, idx) => (
                <td key={idx} style={{ cursor: 'pointer' }} onClick={() => handleInventoryItemClick(item.Inv_Id, null, item.IsPublished)}>{col.cell(item)}</td>
              ))
            }
          />
        ) : (
          !loading &&
          showNoResult &&
          (itemList == null || itemList?.length == 0) && (
            <div className="no-result-wrap no-result-padding">
              <h3>No results found</h3>
              <p>
                We couldn't find any listings that match your filters and search
                terms. Try a different search or reset all filters.
              </p>
            </div>
          )
        )}
      </div>

      {showNoResult && itemList !== null && itemList?.length > 0 && (
        <Paginator
          currentPage={currentPage}
          hasNextPage={hasNextPage}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      <MobileActionModal
        show={actionModal.show}
        onHide={() => setActionModal({ show: false, itemId: null })}
        actions={[
          { label: "Edit", icon: EditIcon, onClick: () => onItemEdit(actionModal.itemId) },
          { label: "Delete", icon: TrashIcon, color: "#dc3545", onClick: () => onItemDelete(actionModal.itemId) },
        ]}
      />
    </div>
  );
};
export default InventoryListing;
